/**
 * BasicInfoStep - Step 1 of view creation wizard
 *
 * Collects basic information: name, description, and tags
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import type { ViewFormData } from '@/pages/ViewCreatePage';

interface BasicInfoStepProps {
  formData: ViewFormData;
  updateFormData: (updates: Partial<ViewFormData>) => void;
}

export const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ formData, updateFormData }) => {
  const [tagInput, setTagInput] = React.useState('');

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFormData({ name: e.target.value });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormData({ description: e.target.value });
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(newTag)) {
        updateFormData({ tags: [...formData.tags, newTag] });
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    updateFormData({ tags: formData.tags.filter((tag) => tag !== tagToRemove) });
  };

  const isNameValid = formData.name.length >= 3 && /^[a-zA-Z][a-zA-Z0-9_]*$/.test(formData.name);
  const showNameError = formData.name.length > 0 && !isNameValid;

  return (
    <div className="space-y-6">
      {/* View Name */}
      <div className="space-y-2">
        <Label htmlFor="view-name">
          View Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="view-name"
          placeholder="e.g., sales_summary_2024"
          value={formData.name}
          onChange={handleNameChange}
          className={showNameError ? 'border-destructive' : ''}
          maxLength={100}
        />
        <div className="flex justify-between text-sm">
          <p className="text-muted-foreground">
            Must start with a letter. Only letters, numbers, and underscores allowed (no hyphens or spaces). 3-100 characters.
          </p>
          <span className={`${formData.name.length > 90 ? 'text-warning' : 'text-muted-foreground'}`}>
            {formData.name.length}/100
          </span>
        </div>
        {showNameError && (
          <p className="text-sm text-destructive">
            {formData.name.length < 3
              ? 'Name must be at least 3 characters'
              : 'Name must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)'}
          </p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="view-description">Description (Optional)</Label>
        <Textarea
          id="view-description"
          placeholder="Brief description of what this view contains and its purpose..."
          value={formData.description}
          onChange={handleDescriptionChange}
          rows={4}
          maxLength={1000}
        />
        <div className="flex justify-between text-sm">
          <p className="text-muted-foreground">
            Provide context about the view's purpose and contents
          </p>
          <span className={`${formData.description.length > 900 ? 'text-warning' : 'text-muted-foreground'}`}>
            {formData.description.length}/1000
          </span>
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-2">
        <Label htmlFor="view-tags">Tags (Optional)</Label>
        <Input
          id="view-tags"
          placeholder="Press Enter to add tags..."
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleAddTag}
        />
        <p className="text-sm text-muted-foreground">
          Add tags to organize and filter views. Press Enter after typing each tag.
        </p>

        {/* Tag List */}
        {formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {formData.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="pl-3 pr-2 py-1 flex items-center gap-1">
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                  aria-label={`Remove tag ${tag}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <h4 className="font-medium mb-2">Tips for Naming Views</h4>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>Use descriptive names that indicate the view's purpose</li>
          <li>Include relevant context like time periods or data types</li>
          <li>Follow your organization's naming conventions</li>
          <li>Examples: customer_orders_2024, sales_metrics_daily, inventory_snapshot</li>
        </ul>
      </div>
    </div>
  );
};
